/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useMachine } from '@xstate/react/lib';
import { FormikHelpers } from 'formik';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';
import { StateValue } from 'xstate';

import { MarketContext } from '@toucan/client/global/market';

import {
    RequestPasswordResetMachineAction,
    RequestPasswordResetMachineState,
    requestPasswordResetMachine,
} from '@toucan/client/shared/components/request-password-reset/helpers/request-password-reset.machine';

import { RequestPasswordResetConfirmation } from './components/request-password-reset-confirmation';
import {
    RequestPasswordResetForm,
    RequestPasswordResetFormValues,
} from './components/request-password-reset-form';
import { styles } from './request-password-reset.styles';
import { requestPasswordReset } from './requests';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface RequestPasswordResetProps {
    /**
     * The initial value of the account email
     */
    initialEmail: string;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

const StyledRequestPasswordReset = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const RequestPasswordReset: FunctionComponent<
    RequestPasswordResetProps
> = ({ initialEmail, ...otherProps }: RequestPasswordResetProps) => {
    const { activeMarket } = useContext(MarketContext);
    const [current, send] = useMachine(requestPasswordResetMachine);

    /**
     * Handles the form submission
     */
    async function handleSubmit(
        values: RequestPasswordResetFormValues,
        formActions: FormikHelpers<RequestPasswordResetFormValues>
    ) {
        try {
            // disable form submission
            formActions.setSubmitting(true);
            await requestPasswordReset(values.email, activeMarket.code);
            send(RequestPasswordResetMachineAction.SUBMIT);
        } catch (error: any) {
            // re enable form submission
            formActions.setSubmitting(false);

            // display any validation errors back from the api
            if (error?.validationErrors) {
                formActions.setErrors(error.validationErrors);
            }
        }
    }

    /**
     * Given a state machine state choose the corresponding view.
     *
     * @param state {StateValue} the state machine state
     */
    function view(state: StateValue) {
        switch (state) {
            case RequestPasswordResetMachineState.CONFIRMATION_PENDING:
                return (
                    <RequestPasswordResetConfirmation data-testid="request-password-reset__confirmation" />
                );

            case RequestPasswordResetMachineState.FORM:
            default:
                return (
                    <RequestPasswordResetForm
                        onRequestPasswordReset={handleSubmit}
                        initialEmail={initialEmail}
                        data-testid="request-password-reset__form"
                    />
                );
        }
    }

    return (
        <StyledRequestPasswordReset initialEmail={initialEmail} {...otherProps}>
            {view(current.value)}
        </StyledRequestPasswordReset>
    );
};
